import React, { useState, useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Settings, 
  Database, 
  Bell, 
  Shield,
  Globe,
  Mail,
  Clock,
  Save,
  AlertTriangle,
  CheckCircle,
  Server,
  Key,
  Users,
  FileText,
  Lock
} from 'lucide-react';
import api from '@/services/api';
import { authService } from '@/services/authService';

interface SystemConfig {
  general: {
    system_name: string;
    organization: string;
    timezone: string;
    language: string;
    date_format: string;
    time_format: string;
  };
  security: {
    session_timeout: number;
    password_min_length: number;
    password_require_uppercase: boolean;
    password_require_numbers: boolean;
    password_require_special: boolean;
    two_factor_auth: boolean;
    max_login_attempts: number;
    lockout_duration: number;
  };
  notifications: {
    email_enabled: boolean;
    smtp_host: string;
    smtp_port: number;
    smtp_username: string;
    smtp_password: string;
    smtp_encryption: string;
    from_email: string;
    from_name: string;
    admin_notifications: boolean;
    user_notifications: boolean;
  };
  backup: {
    auto_backup_enabled: boolean;
    backup_frequency: string;
    backup_time: string;
    retention_days: number;
    include_uploads: boolean;
    notification_email: string;
  };
  eurocat: {
    registry_code: string;
    submission_enabled: boolean;
    auto_validation: boolean;
    submission_frequency: string;
    contact_email: string;
    api_endpoint: string;
    api_key: string;
  };
  maintenance: {
    maintenance_mode: boolean;
    maintenance_message: string;
    allowed_ips: string[];
  };
}

export const SystemSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [config, setConfig] = useState<SystemConfig>({
    general: {
      system_name: 'MCC Registry Romania',
      organization: 'Ministerul Sănătății',
      timezone: 'Europe/Bucharest',
      language: 'ro',
      date_format: 'DD/MM/YYYY',
      time_format: '24h'
    },
    security: {
      session_timeout: 30,
      password_min_length: 8,
      password_require_uppercase: true,
      password_require_numbers: true,
      password_require_special: true,
      two_factor_auth: false,
      max_login_attempts: 5,
      lockout_duration: 30
    },
    notifications: {
      email_enabled: true,
      smtp_host: 'smtp.gmail.com',
      smtp_port: 587,
      smtp_username: '',
      smtp_password: '',
      smtp_encryption: 'tls',
      from_email: 'noreply@chdregistry.ro',
      from_name: 'MCC Registry',
      admin_notifications: true,
      user_notifications: true
    },
    backup: {
      auto_backup_enabled: true,
      backup_frequency: 'daily',
      backup_time: '02:00',
      retention_days: 30,
      include_uploads: true,
      notification_email: 'admin@chdregistry.ro'
    },
    eurocat: {
      registry_code: 'RO-MCC',
      submission_enabled: true,
      auto_validation: true,
      submission_frequency: 'quarterly',
      contact_email: 'eurocat@chdregistry.ro',
      api_endpoint: 'https://eu-rd-platform.jrc.ec.europa.eu/eurocat',
      api_key: ''
    },
    maintenance: {
      maintenance_mode: false,
      maintenance_message: 'System is under maintenance. Please try again later.',
      allowed_ips: []
    }
  });

  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser || currentUser.user_level?.level_code !== 'administrator') {
      setIsAuthorized(false);
      setLoading(false);
      return;
    }
    setIsAuthorized(true);
    loadConfiguration();
  };

  const loadConfiguration = async () => {
    try {
      setError(null);
      const response = await api.get('/api/admin/config');
      if (response.data) {
        // Merge API response with default config to ensure all properties exist
        setConfig(prevConfig => ({
          general: {
            ...prevConfig.general,
            ...(response.data.general || {})
          },
          security: {
            ...prevConfig.security,
            ...(response.data.security || {})
          },
          notifications: {
            ...prevConfig.notifications,
            ...(response.data.notifications || {})
          },
          backup: {
            ...prevConfig.backup,
            ...(response.data.backup || {})
          },
          eurocat: {
            ...prevConfig.eurocat,
            ...(response.data.eurocat || {})
          },
          maintenance: {
            ...prevConfig.maintenance,
            ...(response.data.maintenance || {})
          }
        }));
      }
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading configuration:', err);
      // Use default config if API fails
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.put('/api/admin/config', config);
      setSuccess('Configurația a fost salvată cu succes');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Eroare la salvarea configurației');
    } finally {
      setSaving(false);
    }
  };

  const testEmailConfiguration = async () => {
    try {
      await api.post('/api/admin/test-email', {
        to: config.notifications.from_email,
        subject: 'Test Email Configuration',
        body: 'This is a test email from MCC Registry'
      });
      setSuccess('Email de test trimis cu succes');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError('Eroare la trimiterea email-ului de test');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </MainLayout>
    );
  }

  if (!isAuthorized) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center space-y-4">
                <div className="p-3 bg-destructive/10 rounded-full">
                  <Lock className="h-8 w-8 text-destructive" />
                </div>
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold">Acces restricționat</h2>
                  <p className="text-muted-foreground">
                    Nu aveți permisiunea de a accesa setările sistemului. 
                    Această secțiune este disponibilă doar pentru administratori.
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => window.location.href = '/dashboard'}
                  className="mt-4"
                >
                  Înapoi la Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Setări sistem</h1>
            <p className="text-muted-foreground">
              Configurează setările generale ale sistemului
            </p>
          </div>
          <Button onClick={saveConfiguration} disabled={saving}>
            {saving ? (
              <>
                <Clock className="mr-2 h-4 w-4 animate-spin" />
                Salvare...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Salvează modificările
              </>
            )}
          </Button>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {/* Settings Tabs */}
        <Tabs defaultValue="general" className="space-y-4">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="security">Securitate</TabsTrigger>
            <TabsTrigger value="notifications">Notificări</TabsTrigger>
            <TabsTrigger value="backup">Backup</TabsTrigger>
            <TabsTrigger value="eurocat">EUROCAT</TabsTrigger>
            <TabsTrigger value="maintenance">Mentenanță</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="mr-2 h-5 w-5" />
                  Setări generale
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="system_name">Nume sistem</Label>
                    <Input
                      id="system_name"
                      value={config.general?.system_name || ''}
                      onChange={(e) => setConfig({
                        ...config,
                        general: { ...config.general, system_name: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="organization">Organizație</Label>
                    <Input
                      id="organization"
                      value={config.general?.organization || ''}
                      onChange={(e) => setConfig({
                        ...config,
                        general: { ...config.general, organization: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="timezone">Fus orar</Label>
                    <Select
                      value={config.general?.timezone || 'Europe/Bucharest'}
                      onValueChange={(value) => setConfig({
                        ...config,
                        general: { ...config.general, timezone: value }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Europe/Bucharest">Europe/București</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="language">Limbă</Label>
                    <Select
                      value={config.general?.language || 'ro'}
                      onValueChange={(value) => setConfig({
                        ...config,
                        general: { ...config.general, language: value }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ro">Română</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Settings */}
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Shield className="mr-2 h-5 w-5" />
                  Setări securitate
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="session_timeout">Timeout sesiune (minute)</Label>
                    <Input
                      id="session_timeout"
                      type="number"
                      value={config.security.session_timeout}
                      onChange={(e) => setConfig({
                        ...config,
                        security: { ...config.security, session_timeout: parseInt(e.target.value) }
                      })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="password_min_length">Lungime minimă parolă</Label>
                    <Input
                      id="password_min_length"
                      type="number"
                      value={config.security.password_min_length}
                      onChange={(e) => setConfig({
                        ...config,
                        security: { ...config.security, password_min_length: parseInt(e.target.value) }
                      })}
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="uppercase">Necesită litere mari</Label>
                    <Switch
                      id="uppercase"
                      checked={config.security.password_require_uppercase}
                      onCheckedChange={(checked) => setConfig({
                        ...config,
                        security: { ...config.security, password_require_uppercase: checked }
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="numbers">Necesită numere</Label>
                    <Switch
                      id="numbers"
                      checked={config.security.password_require_numbers}
                      onCheckedChange={(checked) => setConfig({
                        ...config,
                        security: { ...config.security, password_require_numbers: checked }
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="special">Necesită caractere speciale</Label>
                    <Switch
                      id="special"
                      checked={config.security.password_require_special}
                      onCheckedChange={(checked) => setConfig({
                        ...config,
                        security: { ...config.security, password_require_special: checked }
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="2fa">Autentificare în doi pași</Label>
                    <Switch
                      id="2fa"
                      checked={config.security.two_factor_auth}
                      onCheckedChange={(checked) => setConfig({
                        ...config,
                        security: { ...config.security, two_factor_auth: checked }
                      })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notification Settings */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Mail className="mr-2 h-5 w-5" />
                  Setări notificări email
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <Label htmlFor="email_enabled">Email activat</Label>
                  <Switch
                    id="email_enabled"
                    checked={config.notifications.email_enabled}
                    onCheckedChange={(checked) => setConfig({
                      ...config,
                      notifications: { ...config.notifications, email_enabled: checked }
                    })}
                  />
                </div>
                {config.notifications.email_enabled && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="smtp_host">Server SMTP</Label>
                        <Input
                          id="smtp_host"
                          value={config.notifications.smtp_host}
                          onChange={(e) => setConfig({
                            ...config,
                            notifications: { ...config.notifications, smtp_host: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="smtp_port">Port SMTP</Label>
                        <Input
                          id="smtp_port"
                          type="number"
                          value={config.notifications.smtp_port}
                          onChange={(e) => setConfig({
                            ...config,
                            notifications: { ...config.notifications, smtp_port: parseInt(e.target.value) }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="smtp_username">Utilizator SMTP</Label>
                        <Input
                          id="smtp_username"
                          value={config.notifications.smtp_username}
                          onChange={(e) => setConfig({
                            ...config,
                            notifications: { ...config.notifications, smtp_username: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="smtp_password">Parolă SMTP</Label>
                        <Input
                          id="smtp_password"
                          type="password"
                          value={config.notifications.smtp_password}
                          onChange={(e) => setConfig({
                            ...config,
                            notifications: { ...config.notifications, smtp_password: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={testEmailConfiguration}
                      className="mt-4"
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      Testează configurația email
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backup Settings */}
          <TabsContent value="backup">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Database className="mr-2 h-5 w-5" />
                  Setări backup
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <Label htmlFor="auto_backup">Backup automat</Label>
                  <Switch
                    id="auto_backup"
                    checked={config.backup.auto_backup_enabled}
                    onCheckedChange={(checked) => setConfig({
                      ...config,
                      backup: { ...config.backup, auto_backup_enabled: checked }
                    })}
                  />
                </div>
                {config.backup.auto_backup_enabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="backup_frequency">Frecvență</Label>
                      <Select
                        value={config.backup.backup_frequency}
                        onValueChange={(value) => setConfig({
                          ...config,
                          backup: { ...config.backup, backup_frequency: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Zilnic</SelectItem>
                          <SelectItem value="weekly">Săptămânal</SelectItem>
                          <SelectItem value="monthly">Lunar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="backup_time">Ora</Label>
                      <Input
                        id="backup_time"
                        type="time"
                        value={config.backup.backup_time}
                        onChange={(e) => setConfig({
                          ...config,
                          backup: { ...config.backup, backup_time: e.target.value }
                        })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="retention_days">Păstrare (zile)</Label>
                      <Input
                        id="retention_days"
                        type="number"
                        value={config.backup.retention_days}
                        onChange={(e) => setConfig({
                          ...config,
                          backup: { ...config.backup, retention_days: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="notification_email">Email notificare</Label>
                      <Input
                        id="notification_email"
                        type="email"
                        value={config.backup.notification_email}
                        onChange={(e) => setConfig({
                          ...config,
                          backup: { ...config.backup, notification_email: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EUROCAT Settings */}
          <TabsContent value="eurocat">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Globe className="mr-2 h-5 w-5" />
                  Setări EUROCAT
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <Label htmlFor="submission_enabled">Trimitere automată</Label>
                  <Switch
                    id="submission_enabled"
                    checked={config.eurocat.submission_enabled}
                    onCheckedChange={(checked) => setConfig({
                      ...config,
                      eurocat: { ...config.eurocat, submission_enabled: checked }
                    })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="registry_code">Cod registru</Label>
                    <Input
                      id="registry_code"
                      value={config.eurocat.registry_code}
                      onChange={(e) => setConfig({
                        ...config,
                        eurocat: { ...config.eurocat, registry_code: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="submission_frequency">Frecvență trimitere</Label>
                    <Select
                      value={config.eurocat.submission_frequency}
                      onValueChange={(value) => setConfig({
                        ...config,
                        eurocat: { ...config.eurocat, submission_frequency: value }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quarterly">Trimestrial</SelectItem>
                        <SelectItem value="biannual">Semestrial</SelectItem>
                        <SelectItem value="annual">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="contact_email">Email contact</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={config.eurocat.contact_email}
                      onChange={(e) => setConfig({
                        ...config,
                        eurocat: { ...config.eurocat, contact_email: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="api_endpoint">API Endpoint</Label>
                    <Input
                      id="api_endpoint"
                      value={config.eurocat.api_endpoint}
                      onChange={(e) => setConfig({
                        ...config,
                        eurocat: { ...config.eurocat, api_endpoint: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Maintenance Settings */}
          <TabsContent value="maintenance">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Server className="mr-2 h-5 w-5" />
                  Mod mentenanță
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label htmlFor="maintenance_mode">Mod mentenanță</Label>
                    <p className="text-sm text-muted-foreground">
                      Activează pentru a bloca accesul utilizatorilor
                    </p>
                  </div>
                  <Switch
                    id="maintenance_mode"
                    checked={config.maintenance.maintenance_mode}
                    onCheckedChange={(checked) => setConfig({
                      ...config,
                      maintenance: { ...config.maintenance, maintenance_mode: checked }
                    })}
                  />
                </div>
                {config.maintenance.maintenance_mode && (
                  <>
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        Modul mentenanță este activ. Utilizatorii nu pot accesa sistemul.
                      </AlertDescription>
                    </Alert>
                    <div>
                      <Label htmlFor="maintenance_message">Mesaj mentenanță</Label>
                      <Input
                        id="maintenance_message"
                        value={config.maintenance.maintenance_message}
                        onChange={(e) => setConfig({
                          ...config,
                          maintenance: { ...config.maintenance, maintenance_message: e.target.value }
                        })}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};